import React, { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Spinner from '@dashboard/components/Spinner';
import { Alert, AlertDescription } from '@dashboard/components/ui/alert';
import EmptyState from '@dashboard/components/EmptyState';
import { chargeEmailLogsKey } from '@dashboard/lib/queryKeys';
import { DataGrid } from '@dashboard/components/DataGrid';
import { getEmailLogColumns, GUEST_REMINDER_LOG_TYPE } from '@dashboard/components/EmailLogColumns';
import Link from '@dashboard/components/Link';
import { useLocation } from 'react-router-dom';
import EmailCustomerDialog from '@dashboard/components/EmailCustomerDialog';
import { buildEmailVariables } from '@dashboard/lib/emailVariables';
import { isBefore } from 'date-fns';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import { Button } from '@dashboard/components/ui/button';
import {
  ResendConfirmationButton,
  resendConfirmationOffReason,
} from '@dashboard/components/OrderPage';
import { confirm } from '@/dashboard/lib/confirm';
import { toast } from '@/dashboard/context/ToastContext';
import { ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';

const FILTERS = { [GUEST_REMINDER_LOG_TYPE]: 'attendee reminders' };

const PaymentsBookingsDetailEmailLogs = ({ payment }) => {
  const { sellerId, token } = useAuth();
  const location = useLocation();
  const filterType = new URLSearchParams(location.search).get('type');
  const filterLabel = FILTERS[filterType];

  const {
    data: emailLogs,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: chargeEmailLogsKey(payment._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/email-logs?chargeId=${payment._id}`, token),
    enabled: !!payment?._id,
  });

  const { mutate: sendChargeConfirmationEmail, isPending: isSendingChargeConfirmationEmail } =
    useMutation({
      mutationFn: async () => {
        return await fetcher(
          `/sellers/${sellerId}/charges/${payment._id}/send-confirmation-email`,
          token,
          {
            method: 'POST',
          },
        );
      },
      onSuccess: () => {
        refetch();
        toast({
          title: 'Confirmation email resent',
        });
      },
      onError: error => {
        refetch();
        toast({
          title: 'Failed to send confirmation email',
          description: error?.message,
          type: 'error',
        });
      },
    });

  async function handleSendChargeConfirmationEmail() {
    const confirmed = await confirm('Send a new confirmation email to the customer?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendChargeConfirmationEmail();
    }
  }

  const { mutate: sendChargeNotificationEmail, isPending: isSendingChargeNotificationEmail } =
    useMutation({
      mutationFn: async () => {
        return await fetcher(
          `/sellers/${sellerId}/charges/${payment._id}/send-notification-email`,
          token,
          {
            method: 'POST',
          },
        );
      },
      onSuccess: () => {
        refetch();
        toast({
          title: 'Notification email resent',
        });
      },
      onError: error => {
        refetch();
        toast({
          title: 'Failed to send notification email',
          description: error?.message,
          type: 'error',
        });
      },
    });

  async function handleSendChargeNotificationEmail() {
    const confirmed = await confirm('Send a new notification email to you and your team?', {
      confirm: 'Send',
    });

    if (confirmed) {
      sendChargeNotificationEmail();
    }
  }

  const columns = useMemo(() => getEmailLogColumns(), []);
  const isLegacyUntrackedPeriod = isBefore(new Date(payment.createdAt), new Date('2024-12-17'));
  const emptyState = (
    <EmptyState>
      <EmptyState.Subtitle>
        {isLegacyUntrackedPeriod
          ? 'Email tracking unavailable before December 17, 2024'
          : 'No emails found for this booking.'}
      </EmptyState.Subtitle>
    </EmptyState>
  );

  return (
    <div className="flex flex-col lg:flex-row items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4 pt-2">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader className="mb-4 gap-2">
            <CardHeaderTitle>Emails</CardHeaderTitle>
            <div className="flex flex-wrap items-center gap-2">
              <ResendConfirmationButton
                offReason={resendConfirmationOffReason(payment, 'booking')}
                onClick={handleSendChargeConfirmationEmail}
                sending={isSendingChargeConfirmationEmail}
              />

              <Button
                variant="outline"
                size="sm"
                onClick={handleSendChargeNotificationEmail}
                disabled={isSendingChargeNotificationEmail || isAbandonedAbandonment(payment)}
              >
                Resend notification
                {isSendingChargeNotificationEmail ? <Spinner /> : <ArrowPathIcon />}
              </Button>
              <EmailCustomerDialog
                asButton
                customerEmail={payment.customer?.email}
                livemode={payment.livemode}
                objectLabel="bookings"
                endpoint={`/sellers/${sellerId}/charges/${payment._id}/send-email`}
                variables={buildEmailVariables({ page: payment.page })}
                queryKey={chargeEmailLogsKey(payment._id)}
                emailsPath={`/bookings/${payment._id}/emails`}
              />
            </div>
          </CardHeader>

          <CardContent>
            {isError ? (
              <Alert variant="destructive" className="my-4">
                <ExclamationCircleIcon />
                <AlertDescription>Failed to load emails</AlertDescription>
              </Alert>
            ) : (
              <>
                {filterLabel ? (
                  <div className="pb-3 text-sm text-gray-700 dark:text-muted-foreground">
                    Showing {filterLabel} only.{' '}
                    <Link to={location.pathname} variant="secondary">
                      Show all emails
                    </Link>
                  </div>
                ) : null}
                <DataGrid
                  columns={columns}
                  data={
                    filterLabel
                      ? (emailLogs ?? []).filter(log => log.type === filterType)
                      : (emailLogs ?? [])
                  }
                  isLoading={isLoading}
                  loadingRows={3}
                  emptyState={emptyState}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentsBookingsDetailEmailLogs;
